import React, { useEffect, useRef } from "react";
import { Button } from "./ui/button";
import { LangSwitcher } from "./LangSwitcher";

const FOCUSABLE =
  'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';

function focusableIn(root) {
  return [...root.querySelectorAll(FOCUSABLE)].filter(
    (el) => !el.disabled && el.getAttribute("aria-hidden") !== "true"
  );
}

export function IntroOverlay({ t, lang, setLang, onClose }) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previousFocusRef = useRef(null);

  useEffect(() => {
    previousFocusRef.current = document.activeElement;
    const dialog = dialogRef.current;
    if (!dialog) return undefined;

    (closeBtnRef.current || dialog).focus();

    const onKey = (e) => {
      if (e.key === "Escape") {
        onClose();
        return;
      }
      if (e.key !== "Tab") return;
      const list = focusableIn(dialog);
      if (!list.length) {
        e.preventDefault();
        dialog.focus();
        return;
      }
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const prev = previousFocusRef.current;
      if (prev && typeof prev.focus === "function") prev.focus();
    };
  }, [onClose]);

  return (
    <div className="fixed inset-0 z-50 bg-black/80 flex items-center justify-center p-4">
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="intro-title"
        tabIndex={-1}
        className="w-full max-w-lg bg-[#0d0d10] border border-zinc-800 rounded-md p-6 shadow-[0_24px_80px_rgba(0,0,0,0.65)] text-zinc-200"
      >
        <LangSwitcher lang={lang} setLang={setLang} />
        <h2
          id="intro-title"
          className="mt-4 text-xl font-bold tracking-tight text-white"
          style={{ fontFamily: "Chivo, sans-serif" }}
        >
          {t("appTitle")}
        </h2>
        <p className="mt-3 text-sm text-zinc-300 leading-relaxed">{t("introLead")}</p>
        <ul className="mt-4 space-y-2 text-[13px] text-zinc-400 list-disc pl-5 leading-relaxed">
          <li>{t("introBulletScreen")}</li>
          <li>{t("introBulletRoles")}</li>
          <li>{t("introBulletTime")}</li>
        </ul>
        <Button
          ref={closeBtnRef}
          data-testid="intro-close"
          onClick={onClose}
          className="mt-6 w-full h-10 bg-sky-500 hover:bg-sky-400 text-white rounded-sm"
        >
          {t("introClose")}
        </Button>
      </div>
    </div>
  );
}
